import { Navigate, RouteObject } from "react-router-dom";

import { canViewSitewideAnalytics } from "@src/components/LimitAccess/check-analytics-access";
import { ScopedRoute } from "@src/components/LimitAccess/ScopedRoute";
import { SitewideAnalyticsLayout } from "@src/features/analytics/sitewideAnalytics/routes/SitewideAnalyticsLayout";

import { EXPERIENCE_ROUTES, GENERAL_ROUTES } from "../../routes";

export const GeneralRoutes: RouteObject[] = [
  {
    path: GENERAL_ROUTES.ConfigHandler,
    lazy: async () => {
      const { ConfigHandler } =
        await import("../../../domains/Settings/ConfigHandler");
      return { element: <ConfigHandler /> };
    },
  },
  {
    path: GENERAL_ROUTES.Index,
    lazy: async () => {
      const { HomeDashboard } = await import("../../../domains/HomeDashboard");
      return { element: <HomeDashboard /> };
    },
  },
  {
    path: GENERAL_ROUTES.Home,
    lazy: async () => {
      const { HomeDashboard } = await import("../../../domains/HomeDashboard");
      return { element: <HomeDashboard /> };
    },
  },
  {
    path: GENERAL_ROUTES.Readme_Login,
    lazy: async () => {
      const { ReadMeLogin } = await import("../../../domains/ReadMe/login");
      return { element: <ReadMeLogin /> };
    },
  },
  {
    path: GENERAL_ROUTES.Logout,
    lazy: async () => {
      const { LogoutRedirect } =
        await import("../../../domains/Redirects/LogoutRedirect");
      return { element: <LogoutRedirect /> };
    },
  },
  // {
  //   path: GENERAL_ROUTES.IntelligemsAi,
  //   lazy: async () => {
  //     const { IntelligemsAi } = await import("../../../domains/IntelligemsAi");
  //     return { element: <IntelligemsAi /> };
  //   },
  // },
  {
    path: GENERAL_ROUTES.Performance,
    lazy: async () => {
      const { PerformanceRoute } =
        await import("../../../domains/Performance/PerformanceRoute");
      return { element: <PerformanceRoute /> };
    },
  },
  {
    path: GENERAL_ROUTES.Integrations,
    lazy: async () => {
      const { IntegrationsRoute } =
        await import("../../../features/integrations/Integrations.route");
      return { element: <IntegrationsRoute /> };
    },
  },
  {
    path: GENERAL_ROUTES.GoogleIntegrationCallback,
    lazy: async () => {
      const { GoogleCallbackRoute } =
        await import("@src/features/integrations/Google/GoogleCallback");
      return { element: <GoogleCallbackRoute /> };
    },
  },
  {
    path: GENERAL_ROUTES.ConfigCompare,
    lazy: async () => {
      const { ConfigCompareDomain } =
        await import("../../../domains/ConfigCompare");
      return { element: <ConfigCompareDomain /> };
    },
  },
  {
    path: GENERAL_ROUTES.ManagePlans,
    lazy: async () => {
      const { ManagePlansDomain } =
        await import("../../../domains/ManagePlans");
      return { element: <ManagePlansDomain /> };
    },
  },
  {
    path: GENERAL_ROUTES.EventsManager,
    lazy: async () => {
      const { EventsManagerRoute } =
        await import("@src/features/eventsManager/EventsManager.route");
      return {
        element: (
          <ScopedRoute
            debugId={GENERAL_ROUTES.EventsManager}
            isEnabled={() => true}
            adminOnly={true}
            children={EventsManagerRoute}
          />
        ),
      };
    },
  },
  // Legacy offers list → merchandising module table
  {
    path: GENERAL_ROUTES.Offers,
    element: (
      <Navigate to={EXPERIENCE_ROUTES.Experiences_Merchandising} replace />
    ),
  },
  {
    path: GENERAL_ROUTES.SitewideAnalytics,
    element: <SitewideAnalyticsLayout />,
  },
  {
    path: GENERAL_ROUTES.SitewideAnalytics_Id,
    element: <SitewideAnalyticsLayout />,
    children: [
      {
        index: true,
        lazy: async () => {
          const { SitewideAnalyticsHome } =
            await import("@src/features/analytics/sitewideAnalytics/routes/SitewideAnalyticsHome.route");
          return {
            element: (
              <ScopedRoute
                debugId={GENERAL_ROUTES.SitewideAnalytics_Id}
                isEnabled={({ org, isAdmin }) =>
                  canViewSitewideAnalytics(org, isAdmin)
                }
                children={SitewideAnalyticsHome}
              />
            ),
          };
        },
      },
      {
        path: "audience",
        lazy: async () => {
          const { SitewideAnalyticsAudience } =
            await import("@src/features/analytics/sitewideAnalytics/routes/SitewideAnalyticsAudience.route");
          return {
            element: (
              <ScopedRoute
                debugId={GENERAL_ROUTES.SitewideAnalyticsAudience_Id}
                isEnabled={({ org, isAdmin }) =>
                  canViewSitewideAnalytics(org, isAdmin)
                }
                children={SitewideAnalyticsAudience}
              />
            ),
          };
        },
      },
      {
        path: "conversion",
        lazy: async () => {
          const { SitewideAnalyticsConversion } =
            await import("@src/features/analytics/sitewideAnalytics/routes/SitewideAnalyticsConversion.route");
          return {
            element: (
              <ScopedRoute
                debugId={GENERAL_ROUTES.SitewideAnalyticsConversion_Id}
                isEnabled={({ org, isAdmin }) =>
                  canViewSitewideAnalytics(org, isAdmin)
                }
                children={SitewideAnalyticsConversion}
              />
            ),
          };
        },
      },
      {
        path: "holiday-benchmark",
        lazy: async () => {
          const { SitewideAnalyticsHolidayBenchmark } =
            await import("@src/features/analytics/sitewideAnalytics/SitewideAnalyticsHolidayBenchmark.route");
          return {
            element: (
              <ScopedRoute
                debugId={GENERAL_ROUTES.SitewideAnalyticsHolidayBenchmark_Id}
                isEnabled={({ org, isAdmin }) =>
                  canViewSitewideAnalytics(org, isAdmin)
                }
                children={SitewideAnalyticsHolidayBenchmark}
              />
            ),
          };
        },
      },
      {
        path: "order-distribution",
        lazy: async () => {
          const { SitewideAnalyticsOrderDistribution } =
            await import("@src/features/analytics/sitewideAnalytics/routes/SitewideAnalyticsOrderDistribution.route");
          return {
            element: (
              <ScopedRoute
                debugId={GENERAL_ROUTES.SitewideAnalyticsOrderDistribution_Id}
                isEnabled={({ org, isAdmin }) =>
                  canViewSitewideAnalytics(org, isAdmin)
                }
                children={SitewideAnalyticsOrderDistribution}
              />
            ),
          };
        },
      },
      {
        path: "performance",
        lazy: async () => {
          const { SitewideAnalyticsPerformance } =
            await import("@src/features/analytics/sitewideAnalytics/routes/SitewideAnalyticsPerformance.route");
          return {
            element: (
              <ScopedRoute
                debugId={GENERAL_ROUTES.SitewideAnalyticsPerformance_Id}
                isEnabled={({ org, isAdmin }) =>
                  canViewSitewideAnalytics(org, isAdmin)
                }
                children={SitewideAnalyticsPerformance}
              />
            ),
          };
        },
      },
    ],
  },
  {
    path: GENERAL_ROUTES.GetHelp,
    lazy: async () => {
      const { GetHelpRoute } =
        await import("@src/features/support/GetHelp.route");
      return { element: <GetHelpRoute /> };
    },
  },
  {
    path: GENERAL_ROUTES.GetInspired,
    lazy: async () => {
      const { GetInspiredRoute } =
        await import("@src/features/getInspired/GetInspired.route");
      return { element: <GetInspiredRoute /> };
    },
  },
  {
    path: GENERAL_ROUTES.GetInspiredPageById,
    lazy: async () => {
      const { GitBookViewerRoute } =
        await import("@src/features/getInspired/GitBookViewer.route");
      return { element: <GitBookViewerRoute /> };
    },
  },
  {
    path: GENERAL_ROUTES.GetInspiredYouTube,
    lazy: async () => {
      const { YouTubeViewerRoute } =
        await import("@src/features/getInspired/YouTubeViewer.route");
      return { element: <YouTubeViewerRoute /> };
    },
  },
  {
    path: GENERAL_ROUTES.GetInspiredDoc,
    lazy: async () => {
      const { GitBookViewerRoute } =
        await import("@src/features/getInspired/GitBookViewer.route");
      return { element: <GitBookViewerRoute /> };
    },
  },
];
